import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'localization';

export interface ItemCarouselMoleculeProps {
  title: string;
  children: React.ReactNode;
  onViewAll?: () => void;
  className?: string;
}

const ItemCarouselMolecule: React.FC<ItemCarouselMoleculeProps> = ({
  title,
  children,
  onViewAll,
  className = '',
}) => {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    checkScrollState();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', checkScrollState, { passive: true });
    const observer = new ResizeObserver(checkScrollState);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', checkScrollState);
      observer.disconnect();
    };
  }, [checkScrollState]);

  const scroll = useCallback((direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollAmount = el.clientWidth * 0.8;
    el.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  }, []);

  const classes = ['marketplace-item-carousel', className]
    .filter(Boolean)
    .join(' ');

  return (
    <section className={classes}>
      <div className="marketplace-item-carousel__header">
        <h3 className="marketplace-item-carousel__title">{title}</h3>
        <div className="marketplace-item-carousel__actions">
          {onViewAll && (
            <button
              type="button"
              className="marketplace-item-carousel__view-all"
              onClick={onViewAll}
            >
              {t('marketplace.browse.viewAll')}
            </button>
          )}
          {canScrollLeft && (
            <button
              type="button"
              className="marketplace-item-carousel__scroll-btn"
              aria-label={t('marketplace.browse.scrollLeft')}
              onClick={() => scroll('left')}
            >
              {'\u2039'}
            </button>
          )}
          {canScrollRight && (
            <button
              type="button"
              className="marketplace-item-carousel__scroll-btn"
              aria-label={t('marketplace.browse.scrollRight')}
              onClick={() => scroll('right')}
            >
              {'\u203A'}
            </button>
          )}
        </div>
      </div>
      <div className="marketplace-item-carousel__track" ref={scrollRef}>
        {children}
      </div>
    </section>
  );
};

export default ItemCarouselMolecule;
